class SearchRequestBuilder {

    SEARCH_URL          = 'http://api.clerk.io/v2/search/search';
    PREDICTIVE_URL      = 'http://api.clerk.io/v2/search/predictive';
    SUGGESTIONS_URL     = 'https://api.clerk.io/v2/search/suggestions';
    CATEGORIES_URL      = 'https://api.clerk.io/v2/search/categories';
    PAGES_URL           = 'https://api.clerk.io/v2/search/pages';
    FACETS_URL          = 'https://api.clerk.io/v2/product/facets';
    COMPLEMENTARY_URL   = 'https://api.clerk.io/v2/recommendations/complementary';


    constructor() {
        this.query = '';
        this.filter = {};
        this.attributes = [];
        this.facetsAttributes = [];
        this.labels = [];
        this.limit = 0;
        this.offset = 0;
        this.order = "";
        this.products = [];
    }

    static from(url) {
        const params = new URLSearchParams(url.split('?')[1]);
        
        const builder = new SearchRequestBuilder();
        
        if (params.has('query')) 
            builder.query = decodeURIComponent(params.get('query'));
        else if (params.has('q')) 
            builder.query = decodeURIComponent(params.get('q'));
        
        if (params.has('filter')) 
            builder.filter = JSON.parse(decodeURIComponent(params.get('filter')));
        
        if (params.has('order') && params.has('orderby')) {
            builder.order = `${params.get('order')}^${params.get('orderby')}`;
        }
        
        return builder;
    }


    withQuery(query = "") {
        this.query = query;
        return this;
    }

    withfilter(filter = {}) {
        this.filter = filter;
        return this;
    }

    withFacetsAttributes(facetsAttributes = []) {
        this.facetsAttributes = facetsAttributes;
        return this;
    }

    withAttributes(attributes = []) {
        this.attributes = attributes;
        return this;
    }

    withLabels(labels = []) {
        this.labels = labels;
        return this;
    }

    withLimit(limit = 0) {
        this.limit = limit;
        return this;
    }

    withOffset(offset = 0) {
        this.offset = offset;
        return this;
    }

    orderBy(order) {
        this.order = order;
        return this;
    }

    withProducts(products = []) {
        this.products = products;
        return this;
    }

    buildParams() {
        let params = "";
        if (this.query.length > 0) 
            params += `&query=${encodeURIComponent(this.query)}`;
        
        if (Object.keys(this.filter).length !== 0) {
            let filterParts = [];

            Object.keys(this.filter).forEach(key => {
                const filterJson = this.filter[key] || undefined; 
                if (!filterJson)
                    return;

                let values = filterJson.values;
                let type = filterJson.type;

                if (type !== 'range') {
                    values = Array.isArray(values) 
                        ? values
                        : [values];

                    values = values.map(value => `${key} = ${value}`).join(' and ');
                }
                filterParts.push(values);
            })

            params += `&filter=(${filterParts.join(' and ')})`;
        }

        if (this.attributes.length > 0) 
            params += `&attributes=[${this.attributes.map(a => `"${a}"`).join(',')}]`;

        if (this.facetsAttributes.length > 0) 
            params += `&facets=[${this.facetsAttributes.map(a => `"${a}"`).join(',')}]`;
          
        if (this.labels.length > 0) 
            params += `&labels=[${this.labels.map(l => `"${l}"`).join(',')}]`;

        if (this.products.length > 0) 
            params += `&products=[${this.products.map(p => `"${p}"`).join(',')}]`;
         
        if (this.limit > 0) 
            params += `&limit=${this.limit}`;

        if (this.offset > 0)
            params += `&offset=${this.offset}`;

        if (this.order.length > 0) {
            const [order, key] = this.order.split('^', 2);
            params += `&order=${order}&orderby=${key}`;
        }
               
        return params;
    }


    search() {
        return `${this.SEARCH_URL}?key=${window.clerk.api}${this.buildParams()}`;
    }

    predict() {
        return `${this.PREDICTIVE_URL}?key=${window.clerk.api}${this.buildParams()}`;
    }

    suggest() {
        return `${this.SUGGESTIONS_URL}?key=${window.clerk.api}${this.buildParams()}`;
    }

    categories() {
        return `${this.CATEGORIES_URL}?key=${window.clerk.api}${this.buildParams()}`; 
    }

    pages() {
        return `${this.PAGES_URL}?key=${window.clerk.api}${this.buildParams()}`;
    }

    facets() {
        return `${this.FACETS_URL}?key=${window.clerk.api}${this.buildParams()}`;   
    }

    complementary() {
        return `${this.COMPLEMENTARY_URL}?key=${window.clerk.api}${this.buildParams()}`;   
    }
  
    toString() {
        return JSON.stringify({
            query: this.query,
            filter: this.filter,
            attributes: this.attributes,
            labels: this.labels,
            limit: this.limit,
            offset: this.offset,
            order: this.order
        }, null, 2);
    }
}


class SearchPage extends HTMLElement {

    constructor() {
        super();
        this.elements = [];
        this._builder;
        this.isMobile = window.innerWidth < 1024;
    }

    get query() {
        return this.dataset.query;
    }

    get limit() {
        return parseInt(this.dataset.limit);
    }

    get offset() {
        return parseInt(this.dataset.offset);
    }

    get order() {
        return this.dataset.orderBy;
    }

    get facets() {
        return JSON.parse(this.dataset.facets || {});
    }

    get attributes() {
        return JSON.parse(this.dataset.attributes)
    }

    get facetForm() {
        return this.isMobile ? this.querySelector('[data-facets-form-drawer]') : this.querySelector('[data-facets-form]');
    }

    get facetsAttributes() {
        return JSON.parse(this.dataset.facetsAttributes);
    }

    get facetsTitles() {
        return JSON.parse(this.dataset.facetsTitles);
    }

    get builder() {
        if (!this._builder) {
            this._builder = SearchRequestBuilder.from(window.location.href)
                .withAttributes(this.attributes)
                .withFacetsAttributes(this.facetsAttributes)
                .withLimit(this.limit);

            this.dataset.query = this._builder.query;
            this.dataset.facets = JSON.stringify(this._builder.filter);
            this.dataset.orderBy = this._builder.order;
            return this._builder;
        }

        this._builder = new SearchRequestBuilder()
            .withQuery(this.query)
            .withOffset(this.offset)
            .withLimit(this.limit)
            .withfilter(this.facets)
            .withAttributes(this.attributes)
            .withFacetsAttributes(this.facetsAttributes)
            .orderBy(this.order)
        return this._builder;
    }

    async connectedCallback() {
        await this.render();
        this.setListeners();


        if (this.order) 
            this.querySelector('[data-select-order]').value = this.order;
        this.querySelector('[data-search-terms]').textContent = "'" + this.dataset.query + "'";
        
    }

    setListeners() {
        this.querySelector('[data-load-more]').addEventListener('click', () => {
            this.loadMore();
        })

        this.querySelector('[data-select-order]').addEventListener('change', (evt) => {
            this.orderBy(evt.target.value);
        })

        this.querySelector('[data-clear]').addEventListener('click', (evt) => {
            this.clearSearch();
        });

        this.querySelector('[data-toggle-facets]').addEventListener('click', () => {
            this.toggleFacets();
        })

        this.facetForm.addEventListener('change', (evt) => {
            if (evt.target.dataset.ignore) return;
            this.onFacetSelected(new FormData(this.facetForm));
        });

    }

    async render(append = false) {
        const searchResponse = await fetch(this.builder.search());
        const searchResponseJSON = await searchResponse.json();

        this.renderFacets(searchResponseJSON)

        const items = searchResponseJSON.product_data

        SearchPage.publish(items);

        const sectionResponse = await fetch(SearchPage.getSectionRenderUrl(items, 'search-grid'));
        const sectionResponseText = await sectionResponse.text();

        const html = new DOMParser().parseFromString(sectionResponseText, "text/html");

        const currentHtml = append ? this.querySelector('[data-container]').innerHTML : "";

        this.querySelector('[data-search-count]').innerHTML = items.length;
        this.querySelector('[data-container]').innerHTML = currentHtml + html.querySelector('[result]').innerHTML;
        this.querySelector('[data-load-more]').dataset.hasMore = items.length >= this.limit;
    }

    async renderFacets(responseJSON) {
        const activeFacets = this.facets;
        const facetsTitles = this.facetsTitles;

        const isChecked = (facet, attribute) => {
            const selectedFilters = activeFacets?.[attribute]?.values; 
            if (!selectedFilters) 
                return false;

            switch (facet.type) {
                case 'range':
                    return selectedFilters === `${attribute}>=${facet.min} and ${attribute}<=${facet.max}`;
                default: 
                    return Array.isArray(selectedFilters) 
                        ? selectedFilters.includes(facet.value.toString())
                        : selectedFilters === facet.value.toString()
            }
        }

        for (let attribute of this.facetsAttributes) {
            const facets = responseJSON.facets[attribute] || {};
            const title = facetsTitles[attribute] || attribute;


            facets.title = title;
            facets.group = attribute;

            if (!responseJSON.facets[attribute])
                continue;

            facets.facets = responseJSON.facets[attribute]
                .map(facet => ({
                    ...facet,
                    checked: isChecked(facet, attribute)
                }));

            facets.type = facets.facets[0]?.type;

            if (facets.type !== 'range') {
                facets.facets = facets.facets.sort((a, b) => {
                    if (a.checked !== b.checked) {
                        return a.checked ? -1 : 1;
                    }
                    return a.name.toString().localeCompare(b.name.toString());
                });
            }

            if (!this.facetForm.querySelector(`search-facets[data-group="${attribute}"`)) {
                this.facetForm.innerHTML += SearchFacets.createFacets(facets);
            }
            publish(PUB_SUB_EVENTS.onSearchFacetsUpdate, {group: attribute, facets: facets });
        }


        
    }


    onFacetSelected(formData) {
        const facets = {};

        for (const key of formData.keys()) {
            facets[key] = facets[key] || {};
            
            const values = formData.getAll(key);
            const fieldset = this.querySelector(`fieldset[data-group="${key}"]`);
            
            facets[key] = {
                values: values.length > 1 ? values : values[0],
                type: fieldset.dataset.type
            };
        }

        this.dataset.facets = JSON.stringify(facets);
        this.render();
        this.pushUrl();
    }

    loadMore() {
        const newOffset = this.limit + this.offset;
        this.dataset.offset = newOffset;
        this.render(true);
    }

    orderBy(order) {
        this.dataset.orderBy = order;
        this.render();
        this.pushUrl();
    }

    clearSearch() {
        this.dataset.facets = '{}';
        this.dataset.orderBy = '';

        this.render();
        this.pushUrl();
    }

    toggleFacets() {
        const aside = this.querySelector('aside');
        const isOpen = aside.dataset.open == 'true';
        aside.dataset.open = !isOpen;

        this.querySelector('[data-toggle-facets]').setAttribute('aria-expanded', !isOpen);
        this.querySelector('[data-toggle-facets]').setAttribute('aria-label', isOpen ? window.accessibilityStrings.showFilters : window.accessibilityStrings.hideFilters);

        const[add, remove] = isOpen ? ['facets-slide-out', 'facets-slide-in'] : ['facets-slide-in', 'facets-slide-out'];

        aside.classList.remove(remove);
        aside.classList.add(add);
    }



    pushUrl() {
        const url = new URL(window.location.href);
    
        url.searchParams.set('query', this.query);

        if (Object.keys(this.facets).length !== 0) {
            url.searchParams.set('filter', JSON.stringify(this.facets));
        } else {
            url.searchParams.delete('filter');
        }

        if (this.order) {
            const [order, key] = this.order.split('^', 2);
            url.searchParams.set('order', order);
            url.searchParams.set('orderby', key);
        } else {
            url.searchParams.delete('order');
            url.searchParams.delete('orderby');
        }

        window.history.pushState({}, '', url.toString());
    }

    static getSectionRenderUrl(items, view) {
        let request = "";
        for (let item of items) request += "&item=" + item.handle;
    
        const rootUrl = window.routes.root_url || "";

        return `${location.origin}${rootUrl}/search?q=${new Date().toString()}&view=${view}${request}`;
    }


    static publish(items) {
        window.Shopify.analytics.publish('search_result', {
            items: items,
        });
    }

}
customElements.define('search-page', SearchPage);

class SearchFacets extends HTMLElement {
    constructor() {
        super();
    }

    get group() {
        return this.dataset.group;
    }

    get showFacetsInput() {
        return this.querySelector('[data-show-facets]');
    }

    get searchFacetsInput() {
        return this.querySelector('[data-search-facets]');
    }

    connectedCallback() {
        this.onSearchFacetsUpdate = subscribe(PUB_SUB_EVENTS.onSearchFacetsUpdate, (event) => {this.onFacetUpdate(event)});


        this.searchFacetsInput && this.searchFacetsInput.addEventListener('input', () => {
            this.filterFacets();
        });

        this.showFacetsInput && this.showFacetsInput.addEventListener('change', () => {
            this.querySelectorAll(`[data-facets="${this.group}"]`).forEach(element => {
                element.classList.toggle('tw-show-more', !this.showFacetsInput.checked);
            })
        });
    }

    filterFacets() {
        const query = this.searchFacetsInput.value.toLocaleLowerCase();
        const facetsContainer = this.querySelector(`[data-facet-container]`);

        facetsContainer.querySelectorAll('label').forEach(label => {
            const text = label.querySelector('span').textContent.toLocaleLowerCase();
            label.style.display= (text.length == 0 || text.includes(query)) ? "block" : "none"
        });
    }

    onFacetUpdate(event) {
       if (event.group !== this.dataset.group)
        return;
        

       this.querySelector(`[data-facet-container]`).innerHTML = SearchFacets.getFacetsPill(event.facets);
    }

    static getFacetsPill(facet) {
        let pillsText = "";
        for (let pill of facet.facets) {
            let value = pill.value;
            let name = pill.name;
            if (facet.type === 'range') {
                value = `${facet.group}>=${pill.min} and ${facet.group}<=${pill.max}`;
                const currency = window.money.symbol;
                if (facet.group === 'price') {
                    name = name.split(' ').map(part => {
                        const num = parseInt(part);
                        return isNaN(num) ? part : (currency + num);
                    }).join(' ');
                }
            }  

            const checked = pill.checked ? 'checked' : '';

            pillsText += `
                <label class="tw-m-0 tw-small-pill" data-group="${facet.group}" data-facet-pill for="${facet.group}-${value}">
                    <span class="tw-h-auto tw-text-xs">${name}</span>
                    <input class="tw-hidden" type="checkbox" id="${facet.group}-${value}" name="${facet.group}" value="${value}" ${checked}>
                </label>`
        }

        return pillsText;
    }

    static createFacets(facet) {

        let inputSearch = "";
        let showMore = "";

        if (facet.type !== 'range') {
            inputSearch = `
                <input 
                    type="text" 
                    placeholder="Search for ${facet.group}..." 
                    style="width: calc(100% - 1.8em); margin: 0 0 1em 0; padding: .6em .8em; border: 1px solid #eee; font-size: .9em;" 
                    data-ignore="true"
                    data-search-facets
                >
                <input 
                    type="checkbox" 
                    data-ignore="true"
                    id="clerk-show-more-${facet.group}" 
                    data-show-facets
                    class="clerk-facet-show-more tw-hidden"
                >`
                showMore = `<label data-facets="${facet.group}" for="clerk-show-more-${facet.group}" class="tw-cursor-pointer tw-show-more tw-hidden tw-text-center tw-text-xs tw-text-[#95a5a6]" style="padding-top:1.5rem;padding-bottom:1.5rem">View More</label>`;
        }



        return `<search-facets class="tw-m-0 tw-flex tw-flex-col tw-gap-32" data-group="${facet.group}">
                    <fieldset class="tw-mb-32 tw-p-0" data-group="${facet.group}" data-type=${facet.type}>
                      <legend class="tw-mb-[1.6rem] tw-text-xs tw-font-bold tw-uppercase tw-text-black">${facet.title}</legend>
                      ${inputSearch}
                      <div class="tw-flex tw-flex-wrap tw-gap-8 tw-show-more tw-overflow-y-hidden" data-facet-container data-facets="${facet.group}">
                      </div>
                      ${showMore}
                    </fieldset>
                </search-facets>`
    }
}

customElements.define('search-facets', SearchFacets);